/**
 * JXS 基础入场动画初始化工具。
 *
 * 这个文件负责扫描页面中带有 `jxs-show-animate` 属性的元素，
 * 并自动为它们绑定“进入视口后上浮淡入”的动画效果。
 *
 * 使用方式：
 * - 在 HTML / Liquid 中给容器添加 `jxs-show-animate`：
 *   容器的直接子元素会依次执行上浮淡入动画。
 * - 如果设置 `jxs-show-animate="one"`：
 *   动画会作用在当前元素本身，而不是它的子元素。
 *
 * 设计目标：
 * - 统一管理主题中通用的 reveal / show 动画。
 * - 避免每个 section 重复编写 IntersectionObserver 与 GSAP 动画逻辑。
 * - 支持 Shopify section 动态加载后自动重新初始化动画。
 */

import { gsap, jxsAnimate } from '@jxs/gsap';

/**
 * 用于标记需要自动执行入场动画的元素。
 *
 * 示例：
 * <div jxs-show-animate>
 *   <h2>Title</h2>
 *   <p>Description</p>
 * </div>
 *
 * 示例：
 * <div jxs-show-animate="one">Only animate this element</div>
 */
const ANIMATE_SELECTOR = '[jxs-show-animate]';

/**
 * 初始化标记，避免同一个元素被重复绑定 inView 监听。
 *
 * Shopify 主题编辑器中 section 可能会被重复加载，
 * 页面局部刷新时也可能再次调用 initAnimate()，
 * 因此需要这个标记保证初始化逻辑只执行一次。
 */
const INIT_ATTRIBUTE = 'data-jxs-animate-initialized';

/**
 * 初始化基础入场动画。
 *
 * 这个方法会在指定 root 内查找所有 `[jxs-show-animate]` 元素，
 * 然后完成三件事：
 * 1. 计算真正需要执行动画的目标元素。
 * 2. 先把目标元素透明度设为 0，避免动画触发前元素已经显示。
 * 3. 绑定 inView 监听，进入视口后执行 `jxsAnimate.floatUp()`。
 *
 * @param {ParentNode|Element} [root=document] 查找动画元素的根节点。
 */
export function initAnimate(root = document) {
  const elements = getAnimateElements(root);

  elements.forEach((el) => {
    if (el.getAttribute(INIT_ATTRIBUTE) === 'true') return;

    // jxs-show-animate="one" 表示只动画当前元素；
    // 否则默认动画当前容器的直接子元素，用于标题、图片、按钮等依次入场。
    const animateEl = el.getAttribute('jxs-show-animate') === 'one' ? el : Array.from(el.children);

    // 先隐藏动画元素，避免元素在进入视口前已经可见，
    // 导致触发 floatUp 时出现“先显示、再突然变透明并播放”的闪动感。
    if (gsap) {
      gsap.set(animateEl, {
        opacity: 0,
      });
    }

    // 标记已初始化，防止重复绑定 IntersectionObserver。
    el.setAttribute(INIT_ATTRIBUTE, 'true');

    // 进入视口后执行上浮淡入动画。
    // delay 用于让页面/section 内容加载完成后再开始播放，减少突兀感。
    jxsAnimate.inView({
      el,
      delay: 0.25,
      enterCallback: () => {
        jxsAnimate.floatUp({
          el: animateEl,
        });
      },
    });
  });
}

/**
 * 获取指定 root 内所有需要初始化动画的元素。
 *
 * 如果 root 本身就是 `[jxs-show-animate]`，也会被包含进结果中。
 * 这对于 Shopify section 动态加载很重要，因为 event.target 可能就是 section 根节点。
 *
 * @param {ParentNode|Element} root 查找范围。
 * @returns {Element[]} 需要初始化动画的元素列表。
 */
function getAnimateElements(root) {
  const elements = [];

  if (root instanceof Element && root.matches(ANIMATE_SELECTOR)) {
    elements.push(root);
  }

  if (typeof root.querySelectorAll === 'function') {
    elements.push(...root.querySelectorAll(ANIMATE_SELECTOR));
  }

  return elements;
}

/**
 * 页面就绪后自动初始化动画。
 *
 * 如果脚本执行时 DOM 还没加载完成，则等待 DOMContentLoaded；
 * 如果 DOM 已经可用，则立即初始化。
 */
function initAnimateWhenReady() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAnimate(), { once: true });
    return;
  }

  initAnimate();
}

initAnimateWhenReady();

/**
 * Shopify 主题编辑器中，section 被动态加载时会触发该事件。
 *
 * 这里对新加载的 section 单独执行 initAnimate，
 * 确保主题编辑器里新增、刷新或重新渲染的模块也能正常播放入场动画。
 */
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAnimate(event.target);
  }
});